import { useState } from "react";
import type { CSSProperties } from "react";
import { useCreateFlag } from "@/api/admin/flags";
import { useConfirmedChange } from "@/components/admin/confirmChange";
import { useAdminT } from "@/components/admin/useAdminT";

const ROW: CSSProperties = { display: "flex", alignItems: "center", gap: 12 };
const STATUS: CSSProperties = { fontSize: 12, color: "var(--d-fg-muted)" };
const BTN: CSSProperties = { padding: "6px 12px", borderRadius: 999, border: "none", background: "var(--d-violet)", color: "#fff", cursor: "pointer" };
const BTN_DISABLED: CSSProperties = { ...BTN, background: "var(--d-line)", cursor: "not-allowed" };
const BTN_GHOST: CSSProperties = { padding: "6px 12px", borderRadius: 999, background: "transparent", border: "1px solid var(--d-line)", color: "var(--d-fg)", cursor: "pointer" };
const FIELD: CSSProperties = { padding: "8px 12px", borderRadius: 12, border: "1px solid var(--d-line)", background: "var(--d-ink)", color: "var(--d-fg)", fontFamily: "var(--font-mono)" };
const OVERLAY: CSSProperties = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 };
const SHEET: CSSProperties = { background: "var(--d-ink-2)", borderRadius: 22, padding: 24, width: "calc(100% - 48px)", maxWidth: 480, display: "flex", flexDirection: "column", gap: 12 };

/** «Create flag»: the form of the old Flags page, same POST /api/admin/flags body, with the caution confirm. */
export function CreateFlagSection(): JSX.Element {
  const t = useAdminT();
  const create = useCreateFlag();
  const { confirmChange, modal } = useConfirmedChange();
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  function submit(): void {
    if (!/^[a-z0-9_]{1,64}$/.test(key)) {
      setError(t("flags.key_invalid"));
      return;
    }
    setError(null);
    const body = { key, description: description || undefined };
    confirmChange({ name: t("flags.create"), risk: "caution" }, "no flag", key, () =>
      create.mutate(body, {
        onSuccess: () => {
          setStatus(`${body.key} ✓`);
          setOpen(false);
          setKey("");
          setDescription("");
        },
        onError: (err) => setStatus((err as Error).message),
      }),
    );
  }

  return (
    <section style={ROW}>
      <button style={BTN} onClick={() => setOpen(true)}>
        {t("flags.create")}
      </button>
      {status && (
        <span role="status" style={STATUS}>
          {status}
        </span>
      )}
      {open && (
        <div style={OVERLAY} onClick={() => setOpen(false)}>
          <div style={SHEET} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontFamily: "var(--font-display)" }}>{t("flags.create")}</h3>
            <input style={FIELD} placeholder={t("flags.key")} value={key} onChange={(e) => setKey(e.target.value)} />
            <input
              style={FIELD}
              placeholder={t("flags.description")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            {error && <div style={{ color: "var(--d-ember)", fontSize: 12 }}>{error}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button style={BTN_GHOST} onClick={() => setOpen(false)}>
                {t("common.cancel")}
              </button>
              <button disabled={create.isPending} style={create.isPending ? BTN_DISABLED : BTN} onClick={submit}>
                {t("common.save")}
              </button>
            </div>
          </div>
        </div>
      )}
      {modal}
    </section>
  );
}
